import React, { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import apiClient from '../services/api';

const CHUNK_SIZE = 1024 * 1024; // 1MB
const SINGLE_SHOT_LIMIT = 4 * 1024 * 1024; // files under 4MB go in one request

const AttachmentButton = ({ sessionId, onAttached }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const uploadSingle = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', file.name);
    const response = await apiClient.post(
      `/ai/sessions/${sessionId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  };

  const uploadChunked = async (file) => {
    const { data: start } = await apiClient.post('/ai/uploads/start', {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
    });
    const uploadId = start.upload_id;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const formData = new FormData();
      formData.append('chunk', blob, `part_${i}`);
      await apiClient.post(`/ai/uploads/${uploadId}/chunk?index=${i}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProgress(Math.round(((i + 1) / (totalChunks + 1)) * 100));
    }

    const { data: attachment } = await apiClient.post(`/ai/uploads/${uploadId}/complete`, {
      session_id: sessionId,
      description: file.name,
    });
    setProgress(100);
    return attachment;
  };

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;

    setUploading(true);
    setProgress(0);
    try {
      const attachment = file.size <= SINGLE_SHOT_LIMIT
        ? await uploadSingle(file)
        : await uploadChunked(file);
      if (onAttached) {
        onAttached(attachment);
      }
    } catch (err) {
      console.error('Failed to upload attachment', err);
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="attach-file-btn"
        onClick={handleClick}
        disabled={!sessionId || uploading}
        className="inline-flex items-center justify-center rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-gray-200 hover:bg-black/70 disabled:opacity-50"
      >
        <Paperclip className="h-3 w-3 mr-1" />
        {uploading ? (progress > 0 ? `Uploading ${progress}%` : 'Uploading...') : 'Attach'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.rtf,.md,.json,.js"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
};

export default AttachmentButton;
